import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import parse from "html-react-parser";
import { FaRegCalendarAlt, FaRegClock, FaRegCommentDots } from "react-icons/fa";
import { MdRemoveRedEye } from "react-icons/md";
import ReactPaginate from "react-paginate";
import { getCategoryIdByName } from "../../../utils/categoryResolver";

function ListBerita() {
	const [posts, setPosts] = useState([]);
	const [currentPage, setCurrentPage] = useState(0);
	const postsPerPage = 5;

	useEffect(() => {
		const fetchPosts = async () => {
			try {
				const catId = (await getCategoryIdByName("Berita")) || 2;
				const response = await axios.get(`/api_w/posts`, {
					params: { category_id: catId },
					headers: { db_name: process.env.REACT_APP_DB_NAME },
				});
				const filteredPosts = response.data.data.filter(
					(post) => post.status === 1
				);
				const sortedPosts = [...filteredPosts].sort(
					(a, b) => new Date(b.date) - new Date(a.date)
				);
				setPosts(sortedPosts);
			} catch (error) {
				toast.error(error.response?.data?.message || error.message);
			}
		};
		fetchPosts();
	}, []);

	const formatDate = (dateString) => {
		const options = { year: "numeric", month: "long", day: "numeric" };
		return new Date(dateString).toLocaleDateString("id-ID", options);
	};

	const formatTime = (dateString) => {
		return new Date(dateString).toLocaleTimeString("id-ID", {
			hour: "numeric",
			minute: "numeric",
		});
	};

	const truncateText = (text, maxLength) => {
		if (!text) return "";
		const cleanText = text.replace(/<[^>]*>/g, "");
		return cleanText.length > maxLength
			? cleanText.slice(0, maxLength) + "..."
			: cleanText;
	};

	const offset = currentPage * postsPerPage;
	const currentPosts = posts.slice(offset, offset + postsPerPage);
	const pageCount = Math.ceil(posts.length / postsPerPage);

	const handlePageClick = ({ selected }) => {
		setCurrentPage(selected);
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	return (
		<div>
			<div className="space-y-8">
				{currentPosts.map((post, idx) => (
					<article
						key={post.id}
						className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:border-green-200 transition-all duration-500"
					>
						{/* Image */}
						<div className="relative h-[250px] md:h-[320px] overflow-hidden">
							<img
								className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
								src={post.image}
								alt={post.title}
							/>
							<div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
						</div>

						{/* Content */}
						<div className="p-6 md:p-8">
							{/* Meta info */}
							<div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-500">
								<span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
									<FaRegCalendarAlt className="w-3 h-3" />
									{formatDate(post.date)}
								</span>
								<span className="inline-flex items-center gap-1.5">
									<FaRegClock className="w-3.5 h-3.5 text-gray-400" />
									{formatTime(post.date)} WIB
								</span>
								{post.views && (
									<span className="inline-flex items-center gap-1.5">
										<MdRemoveRedEye className="w-3.5 h-3.5 text-gray-400" />
										{post.views} views
									</span>
								)}
								<span className="inline-flex items-center gap-1.5">
									<FaRegCommentDots className="w-3.5 h-3.5 text-gray-400" />0
									Komentar
								</span>
							</div>

							{/* Title */}
							<Link to={`/berita/${post.slug}`}>
								<h2 className="text-xl md:text-2xl font-bold text-gray-800 group-hover:text-green-700 transition-colors duration-300 mb-3 leading-tight">
									{post.title}
								</h2>
							</Link>

							{/* Description */}
							<p className="text-gray-600 leading-relaxed text-sm md:text-base mb-5">
								{parse(truncateText(post.description, 300))}
							</p>

							{/* Read more */}
							<Link
								to={`/berita/${post.slug}`}
								className="inline-flex items-center gap-2 text-green-700 font-semibold text-sm hover:text-green-800 group/btn transition-colors duration-200"
							>
								Baca Selengkapnya
								<svg
									className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-200"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M17 8l4 4m0 0l-4 4m4-4H3"
									/>
								</svg>
							</Link>
						</div>
					</article>
				))}
			</div>

			{/* Pagination */}
			{pageCount > 1 && (
				<div className="mt-10 flex justify-center">
					<ReactPaginate
						previousLabel={
							<svg
								className="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M15 19l-7-7 7-7"
								/>
							</svg>
						}
						nextLabel={
							<svg
								className="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M9 5l7 7-7 7"
								/>
							</svg>
						}
						breakLabel="..."
						pageCount={pageCount}
						marginPagesDisplayed={1}
						pageRangeDisplayed={3}
						onPageChange={handlePageClick}
						forcePage={currentPage}
						containerClassName="flex items-center gap-1"
						pageClassName="w-10 h-10 flex items-center justify-center rounded-xl text-sm font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
						pageLinkClassName="w-full h-full flex items-center justify-center"
						activeClassName="!bg-green-600 !text-white shadow-lg shadow-green-600/30 hover:!bg-green-700 hover:!text-white"
						previousClassName="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
						previousLinkClassName="w-full h-full flex items-center justify-center"
						nextClassName="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
						nextLinkClassName="w-full h-full flex items-center justify-center"
						breakClassName="w-10 h-10 flex items-center justify-center text-gray-400"
						disabledClassName="opacity-30 cursor-not-allowed hover:bg-transparent"
					/>
				</div>
			)}
		</div>
	);
}

export default ListBerita;
